/**
 * One place that notices an expired session, for the whole portal.
 *
 * WHY THIS IS A GLOBAL WRAPPER AND NOT A SHARED HELPER. Kim hit "Invalid or expired token"
 * trying to save a rate on 2026-09-30. Nothing was broken: she signed in at 17:29 the
 * previous day, access tokens live 720 minutes, so hers died at 05:29 and she tried at
 * 08:46. The defect was that the portal said nothing useful and did nothing about it --
 * `api.js` rethrows the server's `detail` verbatim, and `AdminRates.js` does not even use
 * that client, it calls `fetch` directly in fourteen places. A shared helper would mean
 * rewiring every one of those call sites across every admin page; wrapping `fetch` once
 * covers pages nobody has touched yet, including any added later. Randall chose this
 * explicitly over the helper, to keep the number of edited lines small mid-testing.
 *
 * Without it this recurs EVERY DAY: sign in once, work a shift, and the next morning the
 * first save fails with a message that reads like a server fault.
 *
 * ⭐ IT NOW REFRESHES FIRST (2026-10-02), which removes the interruption instead of
 * explaining it. `POST /api/auth/refresh` has existed with rotation and rate limiting since
 * the auth rewrite and the portal had NEVER called it -- another built-and-dormant feature,
 * the same shape as `photo_aspect_ratio` and `analytics_events`. A 401 now buys one refresh
 * and one replay of the original request; only if that fails does the clear-and-redirect
 * below happen. Kim's refresh token is valid 7 days against a 12-hour access token, so her
 * morning save succeeds instead of bouncing her to a login screen.
 *
 * ⚠️⚠️ ROTATION IS ONE-TIME-USE, WHICH IS WHY THE SINGLE-FLIGHT MATTERS. A page firing six
 * requests at once produces six 401s; without a shared promise each would spend the same
 * refresh token, five would be rejected, and the user would be logged out anyway -- worse
 * than not refreshing, because it would look intermittent.
 *
 * ⚠️ RAISING `ACCESS_TOKEN_EXPIRE_MINUTES` IS NOT THE ALTERNATIVE. It only moves the
 * boundary, the same reasoning that made `dealer_today()` necessary instead of pinning
 * everything to the app server's clock. The interruption is the thing to remove.
 *
 * ⚠️⚠️ LATENT SERVER DEFECT FOUND WHILE VERIFYING THIS, recorded rather than fixed. A refresh
 * performed in the SAME SECOND the access token was issued **kills the session**: JWT `exp`
 * has one-second granularity, so `create_access_token` returns a byte-identical string, and
 * the endpoint then blacklists "the old token" — which is the new one. Reproduced live: the
 * refreshed token came back 401 "Token has been revoked". Two seconds later it is 200.
 * Unreachable from this guard in practice (it only refreshes on a 401, and a token one second
 * old does not produce one) and irrelevant to the real case, where Kim refreshes twelve hours
 * after signing in. The server-side fix is to skip the blacklist when the new token equals the
 * old, in `api/routers/auth.py` around the `_token_blacklist[old_token]` line — a three-line
 * change to shared auth that deserves its own review rather than riding along here.
 */
import { API_BASE } from '../config';

// Cleared on an expired session. `adminRefresh` goes too: leaving a refresh token behind
// while removing the access token is a half-state nothing in the portal reads.
const AUTH_KEYS = ['adminAuth', 'adminRefresh', 'token', 'userType'];

/**
 * Paths where 401 is the CORRECT answer to a legitimate request rather than evidence of an
 * expired session -- a wrong password, or a refresh token that has itself expired. Bouncing
 * on these would fight the login page for control of the URL and hide the real message.
 */
const EXEMPT_PATHS = [
  '/auth/login',
  '/mobile-login',
  '/admin/login',
  '/auth/dealer-login',
  '/auth/dealer/login',
  '/auth/partner-login',
  '/auth/refresh',
];

/** Send people to the login screen for the area they were actually in. */
export function loginPathFor(pathname) {
  if (pathname.startsWith('/admin')) return '/admin/login';
  if (pathname.startsWith('/photographer')) return '/photographer/login';
  if (pathname.startsWith('/partner')) return '/partner/login';
  return '/login';
}

let redirecting = false;

function handleExpiredSession() {
  // A page can have several requests in flight; all of them will see 401. Redirect once.
  if (redirecting) return;
  redirecting = true;

  AUTH_KEYS.forEach((k) => {
    try {
      localStorage.removeItem(k);
    } catch (e) {
      /* private mode / blocked site data -- the redirect below still stands */
    }
  });

  const { pathname, search, hash } = window.location;
  const target = loginPathFor(pathname);
  // `expired=1` is what makes the login page say why it is being shown; `next` lets the
  // login page send them back where they were. Both are additive -- a page that ignores
  // them behaves exactly as it does today.
  const next = encodeURIComponent(pathname + search + hash);
  // replace(), not assign(): the page they were on is dead, so it should not sit in history
  // waiting for a Back press to produce the same failure again.
  window.location.replace(`${target}?expired=1&next=${next}`);
}

function urlOf(input) {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  if (input && typeof input.url === 'string') return input.url; // Request object
  return '';
}

/**
 * ⚠️ ONLY REPLAY A REQUEST WE CAN REPRODUCE EXACTLY.
 *
 * A `Request` object's body is a stream that can be read once, and FormData/Blob/stream
 * bodies cannot be re-sent either -- replaying one of those would send a truncated or empty
 * body, which on a POST means silently writing the wrong thing. That is far worse than the
 * login bounce this is trying to avoid, so anything not provably safe falls through to
 * clear-and-redirect, exactly as before. Every portal call site passes a URL string with a
 * JSON string body, so the safe path covers all of them today.
 */
function isReplayable(input, init) {
  if (typeof input !== 'string' && !(input instanceof URL)) return false;
  const body = init && init.body;
  if (body == null) return true;
  return typeof body === 'string' || body instanceof URLSearchParams;
}

/** Rebuild the headers with the new access token, leaving the caller's object untouched. */
function withFreshToken(init, token) {
  const next = { ...(init || {}) };
  const src = (init && init.headers) || {};
  const headers = {};
  if (src instanceof Headers) {
    src.forEach((v, k) => { headers[k] = v; });
  } else if (Array.isArray(src)) {
    src.forEach(([k, v]) => { headers[k] = v; });
  } else {
    Object.assign(headers, src);
  }
  // Replace whatever casing the caller used rather than adding a second header.
  Object.keys(headers).forEach((k) => {
    if (k.toLowerCase() === 'authorization') delete headers[k];
  });
  headers.Authorization = `Bearer ${token}`;
  next.headers = headers;
  return next;
}

// ⚠️ ONE shared refresh for the whole page, for as long as it is in flight. Cleared
// afterwards so a LATER expiry can refresh again -- this is a lock, not a latch.
let refreshInFlight = null;

function startRefresh(originalFetch) {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    let refresh = null;
    let stale = null;
    try {
      refresh = localStorage.getItem('adminRefresh');
      stale = localStorage.getItem('adminAuth');
    } catch (e) {
      return null;                         // storage blocked -- cannot refresh
    }
    if (!refresh) return null;             // nothing to refresh with
    try {
      const res = await originalFetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // ⚠️ The endpoint declares `Depends(security)` (HTTPBearer, auto_error), so a
          // MISSING header is a 403 before the refresh token is even read. It only decodes
          // this token to blacklist it, inside a try/except, so the EXPIRED one is fine and
          // is exactly what we have.
          Authorization: `Bearer ${stale || 'expired'}`,
        },
        body: JSON.stringify({ refresh_token: refresh }),
      });
      if (!res.ok) return null;            // 401 (refresh itself expired), 429, 5xx
      const data = await res.json();
      if (!data || !data.access_token) return null;
      try {
        localStorage.setItem('adminAuth', data.access_token);
        // Rotation: the old refresh token is now spent, so storing the new one is not
        // optional. Dropping it here would make the NEXT expiry unrecoverable.
        if (data.refresh_token) localStorage.setItem('adminRefresh', data.refresh_token);
      } catch (e) {
        return null;                       // could not persist it -- treat as a failure
      }
      return data.access_token;
    } catch (e) {
      return null;                         // offline, CORS, parse failure
    }
  })().finally(() => { refreshInFlight = null; });
  return refreshInFlight;
}

export function installAuthGuard() {
  // Idempotent: dev hot-reload re-runs module code, and wrapping a wrapper would make every
  // 401 handled twice over.
  if (window.__autolotaAuthGuard) return;
  window.__autolotaAuthGuard = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const response = await originalFetch(input, init);
    // Everything below is best-effort. The guard must never throw where the caller does not
    // expect it; on any internal problem the caller still receives the original 401 and
    // keeps its own error handling.
    try {
      if (response.status !== 401) return response;
      const url = urlOf(input);
      if (!url.startsWith(API_BASE)) return response;          // not ours -- do nothing
      if (EXEMPT_PATHS.some((p) => url.includes(p))) return response;

      // ⭐ ONE refresh, then ONE replay. The retry is bounded by being inside a single
      // wrapper invocation: there is no loop and no counter to get wrong, so a server
      // answering 401 forever costs exactly two requests and then the redirect.
      if (isReplayable(input, init)) {
        const token = await startRefresh(originalFetch);
        if (token) {
          const retried = await originalFetch(input, withFreshToken(init, token));
          // ⚠️ A 401 on the REPLAY means the new token is not the problem -- the session is
          // genuinely finished, or this user may not do this. Redirect rather than loop.
          if (retried.status !== 401) return retried;
        }
      }
      handleExpiredSession();
    } catch (e) {
      /* never let session handling break a working request */
    }
    return response;
  };
}

// Exported for tests: the decision, with no browser or storage involved.
export function shouldRedirect(status, url) {
  if (status !== 401) return false;
  if (!url.startsWith(API_BASE)) return false;
  return !EXEMPT_PATHS.some((p) => url.includes(p));
}
